import { buildMockLiveSearchFillUrl } from './mock-livesearch-fill';

/** @type {string[]} */
const FIRST_NAMES = [
  'Jean', 'Marie', 'Paul', 'Sophie', 'Lucas', 'Emma', 'Thomas', 'Léa',
  'Nicolas', 'Camille', 'Antoine', 'Julie', 'Pierre', 'Claire', 'Hugo',
  'Manon', 'Louis', 'Chloé', 'Arthur', 'Sarah', 'Gabriel', 'Inès',
  'Raphaël', 'Zoé', 'Maxime', 'Élise', 'Alexandre', 'Anaïs', 'Julien',
  'Laura', 'Benjamin', 'Margot', 'Mathieu', 'Pauline', 'Clément', 'Alice',
  'Vincent', 'Charlotte', 'Florian', 'Océane', 'Damien', 'Audrey',
  'Sébastien', 'Marion', 'Guillaume', 'Valentine', 'Olivier', 'Justine',
  'François', 'Amélie', 'Théo', 'Noémie', 'Adrien', 'Lucie', 'Kevin', 'Émilie',
];

/** @type {string[]} */
const LAST_NAMES = [
  'Dupont', 'Martin', 'Bernard', 'Leroy', 'Moreau', 'Simon', 'Laurent',
  'Lefebvre', 'Michel', 'Garcia', 'David', 'Bertrand', 'Roux', 'Vincent',
  'Fournier', 'Girard', 'Bonnet', 'Dupuis', 'Lambert', 'Fontaine', 'Rousseau',
  'Blanc', 'Guerin', 'Muller', 'Henry', 'Roussel', 'Nicolas', 'Perrin',
  'Morin', 'Mathieu', 'Clement', 'Gauthier', 'Dumont', 'Lopez', 'Fabre',
  'Andre', 'Mercier', 'Garnier', 'Faure', 'Chevalier', 'Blanchard', 'Giraud',
  'Leclerc', 'Barbier', 'Arnaud', 'Martinez', 'Gerard', 'Roche', 'Renard',
  'Schmitt', 'Colin', 'Vidal', 'Caron', 'Peters', 'Meyer', 'Weber',
];

/** @type {string[]} */
const ORGANISATIONS = [
  'Heeds', 'Zenith Productions', 'Olympia Events', 'Bataclan Agency',
  'Trabendo Live', 'Accor Arena', 'Philharmonie Paris', 'Cirque National',
  'Festival des Vieilles Charrues', 'Rock en Seine', 'Transmusicales',
  'Arte Concert', 'France Télévisions', 'Radio Nova', 'Fnac Spectacles',
  'Ticketmaster France', 'Shotgun', 'Digitick', 'Eventbrite France',
  'Sacem', 'Adami', 'Spedidam', 'Cnv', 'Ministère de la Culture',
  'Ville de Paris', 'Région Île-de-France', 'Théâtre du Châtelet',
  'Opéra Bastille', 'Comédie Française', 'Maison de la Radio',
];

/**
 * @param {string | null} text
 * @returns {ApiLiveSearchTextWithIcon}
 */
function textWithIcon(text) {
  return { text, icon: null, iconColor: null };
}

/**
 * @param {string | null} libText
 * @param {string | null} valueText
 * @returns {ApiLiveSearchResultCell}
 */
function cell(libText, valueText) {
  return {
    lib: textWithIcon(libText),
    value: textWithIcon(valueText),
  };
}

/**
 * Flatten all value texts from result rows (for mock filtering).
 * @param {ApiLiveSearchResult} result
 * @returns {ApiLiveSearchResultCell[]}
 */
function flattenResultCells(result) {
  return (result.rows ?? []).flatMap((row) => row.values ?? []);
}

/**
 * @param {number} index
 * @returns {ApiLiveSearchResult}
 */
function buildCatalogResult(index) {
  const firstName = FIRST_NAMES[index % FIRST_NAMES.length];
  const lastName = LAST_NAMES[Math.floor(index / FIRST_NAMES.length) % LAST_NAMES.length];
  const organisation = ORGANISATIONS[index % ORGANISATIONS.length];
  const fullName = `${firstName} ${lastName}`;
  const isActive = index % 7 !== 0;
  const isSynced = index % 3 !== 0;
  const isStaff = index % 4 === 0;
  const resultId = `contact-ls-${String(index + 1).padStart(3, '0')}`;

  const emailLocal = `${firstName}.${lastName}`.toLowerCase().replace(/\s+/g, '');
  const email = `${emailLocal}@${organisation.toLowerCase().replace(/\s+/g, '')}.fr`;
  const jobs = [
    'Directeur technique',
    'Chargé de production',
    'Régisseur',
    'Responsable communication',
    'Assistant production',
  ];
  const job = jobs[index % jobs.length];

  return {
    id: resultId,
    title: fullName,
    subTitle: organisation,
    actif: isActive,
    rows: [
      { values: [cell(null, job)] },
      { values: [cell(null, email)] },
      {
        values: [
          cell('Synchronisation', isSynced ? 'oui' : 'non'),
          cell('Actif', isActive ? 'oui' : 'non'),
          cell('Staff', isStaff ? 'oui' : 'non'),
        ],
      },
    ],
    actions: [
      {
        lib: 'Affecter',
        icon: 'fal fa-link',
        fields: [],
        urls: {
          autoSubmitUrl: '/form/submit/live-search-affect',
          fieldsUrl: null,
        },
      },
      {
        lib: 'Copier la structure',
        icon: 'fal fa-home',
        fields: [],
        urls: {
          autoSubmitUrl: null,
          fieldsUrl: buildMockLiveSearchFillUrl(resultId, 'structure'),
        },
      },
      {
        lib: 'Copier l\'individu',
        icon: 'fal fa-user',
        fields: [],
        urls: {
          autoSubmitUrl: null,
          fieldsUrl: buildMockLiveSearchFillUrl(resultId, 'contact'),
        },
      },
      {
        lib: 'Modifier',
        icon: 'fal fa-pen-to-square',
        fields: [],
        urls: {
          autoSubmitUrl: null,
          fieldsUrl: buildMockLiveSearchFillUrl(resultId, 'edit'),
        },
      },
    ],
  };
}

/** @type {ApiLiveSearchResult[]} */
const CATALOG_RESULTS = Array.from({ length: 55 }, (_, index) => buildCatalogResult(index));

/**
 * Full live search catalog (55 results) for mock filtering in development.
 * @type {ApiLiveSearchResponse}
 */
export const mockLiveSearchCatalog = {
  results: CATALOG_RESULTS,
};

/**
 * Build the lowercase haystack used to match a live search field against a result.
 * Mock-only logic: encodes the conventions of this catalog (field ids `ls_name`,
 * `ls_org`). Lives next to the mock so the domain stays generic.
 * @param {ApiLiveSearchResult} result
 * @param {string} fieldId
 * @returns {string}
 */
function getLiveSearchHaystackForField(result, fieldId) {
  if (fieldId === 'ls_org') {
    return (result.subTitle ?? '').toLowerCase();
  }

  if (fieldId === 'ls_name') {
    return [
      result.title,
      ...flattenResultCells(result).map((item) => item.value.text ?? ''),
    ].join(' ').toLowerCase();
  }

  return [
    result.title,
    result.subTitle ?? '',
    ...flattenResultCells(result).map((item) => item.value.text ?? ''),
  ].join(' ').toLowerCase();
}

/**
 * Filter live search catalog results against active search field values (AND).
 * Mock-only: simulates server-side filtering until the real API is wired.
 * @param {ApiLiveSearchResult[]} results
 * @param {ApiLiveSearchRequest} body
 * @returns {ApiLiveSearchResult[]}
 */
export function filterMockLiveSearchResults(results, body) {
  const activeFilters = body.searchFields
    .map((field) => ({
      id: field.id,
      query: field.value.trim().toLowerCase(),
    }))
    .filter((field) => field.query !== '');

  if (activeFilters.length === 0) {
    return [];
  }

  return results.filter((result) => activeFilters.every((filter) => {
    const haystack = getLiveSearchHaystackForField(result, filter.id);
    return haystack.includes(filter.query);
  }));
}
